import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserSecret, FaRightFromBracket } from 'react-icons/fa6';
import { useNavigate } from 'react-router';
import { useLogin } from '../../hooks/useLogin';
import { useStopImpersonation } from '../../api/queryHooks/user';
import { getTokenTimeRemaining } from '../../utils/jwt';
import './ImpersonationBanner.scss';

// Design spec (docs/superpowers/specs/2026-08-19-enduser-impersonation-design.md):
// "a countdown once under ~5 minutes from expiry".
const COUNTDOWN_THRESHOLD_SECONDS = 5 * 60;
// How often the countdown re-checks the token's remaining lifetime. Coarser
// than a real ticking clock — the display only needs minute-level precision
// — and cheap enough to run for as long as a banner is mounted.
const TICK_INTERVAL_MS = 15 * 1000;

/**
 * Always-visible marker that this session is an admin viewing the shop as
 * another user. Mounted by MainLayout for the shop, and — because the
 * backend's role ceiling strips admin roles from every impersonated
 * principal, so an impersonated session can never actually render
 * AdminLayout — by AdminRoute's non-admin branch for `/admin/*` (alongside
 * `Forbidden`, outside both layouts). AdminLayout also mounts this component
 * as defense in depth, but that mount is unreachable for an impersonated
 * session by construction. See AdminRoute.tsx.
 */
const ImpersonationBanner = () => {
  const { t } = useTranslation();
  const { impersonation, userData } = useLogin();
  const stop = useStopImpersonation();
  const navigate = useNavigate();

  // Hooks must run unconditionally (rules of hooks) — the "not impersonating"
  // early return below has to come AFTER every hook call, including this one.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!impersonation) return undefined;
    const id = setInterval(() => forceTick((n) => n + 1), TICK_INTERVAL_MS);
    return () => clearInterval(id);
    // Only re-arm the interval when impersonation starts/stops, not on every
    // tick-triggered re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(impersonation)]);

  if (!impersonation || !userData) return null;

  const remainingSeconds = getTokenTimeRemaining(userData.token);
  const minutesRemaining = Math.ceil(remainingSeconds / 60);
  const showCountdown = remainingSeconds <= COUNTDOWN_THRESHOLD_SECONDS;

  const handleExit = () => {
    const targetId = userData.id;
    stop.mutate(undefined, {
      onSettled: () => {
        navigate(targetId ? `/admin/users/${targetId}` : '/admin/users');
      },
    });
  };

  return (
    <div
      className="impersonation-banner notification is-danger"
      data-testid="impersonation-banner"
      role="status"
    >
      <span className="impersonation-banner__label">
        <FaUserSecret aria-hidden="true" />
        <span className="is-hidden-mobile">
          {t('impersonation.banner', {
            name: userData.name ?? '',
            email: userData.email ?? '',
          })}
        </span>
        <span className="is-hidden-tablet">
          {t('impersonation.banner_short', { name: userData.name ?? '' })}
        </span>
        {showCountdown && (
          <span className="impersonation-banner__countdown">
            {minutesRemaining > 0
              ? t('impersonation.expires_in', { count: minutesRemaining })
              : t('impersonation.expiring')}
          </span>
        )}
      </span>
      <button
        type="button"
        className="button is-small is-light"
        onClick={handleExit}
        disabled={stop.isPending}
      >
        <FaRightFromBracket aria-hidden="true" />
        <span>{t('impersonation.exit')}</span>
      </button>
    </div>
  );
};

export default ImpersonationBanner;
